import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { format, parse } from "date-fns";
import EventCard from "../components/EventCard";
import { getEvents } from "../api/eventsApi";

const MAX_LENGTH = 90;
const ITEMS_PER_PAGE = 6; // how many events per page

const normalizeEvent = (raw, index) => {
  if (!raw || typeof raw !== "object") return null;

  const title = raw.title ? String(raw.title).trim() : "Untitled Event";
  const description = raw.description ? String(raw.description).trim() : "";
  const location = raw.location ? String(raw.location).trim() : "";

  const imageUrl = raw.imageUrl || raw.img || "";
  const otherImages =
    raw.otherImages ||
    raw.OtherImages ||
    ""
      .split(",")
      .map((url) => url.trim())
      .filter(Boolean); // always an array now;

  let dateObj = null;
  if (raw.date) {
    const d = parse(raw.date, "dd/MM/yyyy", new Date());
    if (!isNaN(d.getTime())) dateObj = d;
  }

  return {
    id: raw.id || index,
    title,
    description,
    location,
    imageUrl,
    dateObj,
    otherImages,
  };
};

const Events = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // pagination state
  const [todayPage, setTodayPage] = useState(1);
  const [upcomingPage, setUpcomingPage] = useState(1);
  const [pastPage, setPastPage] = useState(1);

  const navigate = useNavigate();

  const handleSeeMore = (event) => {
    navigate(`/events/${event.id}`, { state: event }); // dynamic route
  };

  const handleCardClick = (event) => {
    navigate(`/events/${event.id}`, { state: event }); // dynamic route
  };

  // Helper function to get a usable image URL (thumbnail for Drive links)
  const getImageUrl = (url) => {
    if (!url) return "";

    // Google Drive link pattern
    const driveMatch = url.match(
      /https:\/\/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)\/?.*/
    );
    if (driveMatch && driveMatch[1]) {
      // Use Google Drive thumbnail
      return `https://drive.google.com/thumbnail?id=${driveMatch[1]}`;
    }

    return url;
  };

  const upcomingRef = useRef(null);
  const pastRef = useRef(null);

  // Handle scroll to the specific section based on URL hash
  useEffect(() => {
    // Get the hash from the URL (e.g., #upcoming, #past)
    const hash = window.location.hash;

    // Add a slight delay to ensure the page is fully loaded
    const scrollTimer = setTimeout(() => {
      if (hash === "#upcoming" && upcomingRef.current) {
        upcomingRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      } else if (hash === "#past" && pastRef.current) {
        pastRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 500);

    return () => clearTimeout(scrollTimer);
  }, []);

  useEffect(() => {
    let mounted = true;
    const fetchEvents = async () => {
      try {
        setLoading(true);
        setError("");
        const response = await getEvents();
        if (!response.success) return;
        if (mounted) {
          const normalized = response.data
            .map((e, i) => normalizeEvent(e, i))
            .filter(Boolean);
          setEvents(normalized);
        }
      } catch {
        if (mounted) setError("Failed to load events. Please try again later.");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchEvents();
    return () => {
      mounted = false;
    };
  }, []);

  // categorize events
  const now = new Date();
  now.setHours(0, 0, 0, 0);

  const todayEvents = events.filter((e) => {
    if (!e.dateObj) return false;
    const d = new Date(e.dateObj);
    d.setHours(0, 0, 0, 0);
    return d.getTime() === now.getTime();
  });

  // const upcomingEvents = events.filter((e) => e.dateObj && e.dateObj > now);
  const upcomingEvents = events
    .filter((e) => e.dateObj && e.dateObj >= now)
    .sort((a, b) => {
      // sort like FeaturedEvents
      const todayTime = now.getTime();
      const aTime = a.dateObj.setHours(0, 0, 0, 0);
      const bTime = b.dateObj.setHours(0, 0, 0, 0);
      if (aTime === todayTime && bTime !== todayTime) return -1;
      if (bTime === todayTime && aTime !== todayTime) return 1;
      return aTime - bTime;
    });
  const pastEvents = events.filter((e) => !e.dateObj || e.dateObj < now);

  // pagination helpers
  const paginate = (arr, page) =>
    arr.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

  const totalPages = (arr) => Math.ceil(arr.length / ITEMS_PER_PAGE);

  const Pagination = ({ currentPage, total, onPageChange }) => (
    <div className="flex justify-center items-center gap-2 mb-16 animate-fadein select-none">
      <button
        disabled={currentPage === 1}
        onClick={() => onPageChange(currentPage - 1)}
        className="flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-blue-100 to-blue-300 text-blue-700 font-bold shadow-md hover:from-blue-200 hover:to-blue-400 transition disabled:opacity-50 disabled:cursor-not-allowed border border-blue-200"
        aria-label="Previous page"
      >
        <svg
          className="w-5 h-5 mr-1"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M15 19l-7-7 7-7"
          />
        </svg>
        Prev
      </button>

      <div className="flex items-center gap-1 px-4 py-2 rounded-full bg-blue-50/80 dark:bg-gray-800/80 shadow border border-blue-100 dark:border-gray-700">
        <span className="text-lg font-bold text-blue-700 dark:text-blue-300 tracking-wider">
          {currentPage}
        </span>
        <span className="mx-1 text-gray-500 dark:text-gray-400 font-semibold">
          /
        </span>
        <span className="text-lg font-bold text-blue-500 dark:text-blue-200 tracking-wider">
          {total}
        </span>
      </div>

      <button
        disabled={currentPage === total}
        onClick={() => onPageChange(currentPage + 1)}
        className="flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-blue-100 to-blue-300 text-blue-700 font-bold shadow-md hover:from-blue-200 hover:to-blue-400 transition disabled:opacity-50 disabled:cursor-not-allowed border border-blue-200"
        aria-label="Next page"
      >
        Next
        <svg
          className="w-5 h-5 ml-1"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>
  );

  return (
    <div className="relative min-h-screen overflow-x-hidden">
      {/* Full-page animated/gradient background */}
      <div
        className="fixed inset-0 -z-10 bg-gradient-to-br from-blue-200 via-blue-100 to-blue-400 dark:from-gray-900 dark:via-gray-950 dark:to-blue-900 animate-fadein"
        aria-hidden="true"
      />
      {/* Main content */}
      <div className="min-h-screen bg-transparent transition-all duration-500">
        {/* Header Section */}
        <section className="pt-32 pb-4 mb-0 relative">
          <div className="absolute inset-0 pointer-events-none select-none">
            <div className="w-full h-full bg-gradient-to-tr from-blue-200/60 via-blue-100/30 to-transparent dark:from-blue-900/40 dark:via-gray-900/30 dark:to-transparent blur-2xl animate-fadein" />
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-extrabold mb-4 text-black tracking-tight drop-shadow-lg animate-fadein font-manrope">
                Events & Activities
              </h1>
            </div>
          </div>
        </section>

        <div className="container mx-auto px-6 relative z-10">
          {loading && (
            <div className="text-center text-blue-500 py-8 animate-pulse">
              Loading events...
            </div>
          )}
          {error && !loading && (
            <div className="text-center text-red-600 py-8 animate-fadein">
              {error}
            </div>
          )}

          {/* Today's Events */}
          {!loading && !error && todayEvents.length > 0 && (
            <section className="py-16 bg-yellow-100/80 backdrop-blur-sm animate-fadein">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <h2 className="text-3xl font-bold mb-8 text-black animate-fadein font-manrope">
                  🎉 Today's Events
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 mb-16 animate-fadein">
                  {paginate(todayEvents, todayPage).map((event) => (
                    <EventCard
                      key={event.id}
                      event={{
                        ...event,
                        imageUrl: getImageUrl(event.imageUrl),
                      }}
                      handleSeeMore={handleSeeMore}
                      MAX_LENGTH={MAX_LENGTH}
                      onClick={() => handleCardClick(event)}
                    />
                  ))}
                </div>
                {totalPages(todayEvents) > 1 && (
                  <Pagination
                    currentPage={todayPage}
                    total={totalPages(todayEvents)}
                    onPageChange={setTodayPage}
                  />
                )}
              </div>
            </section>
          )}

          {/* Upcoming Events */}
          {!loading && !error && (
            <section
              ref={upcomingRef}
              id="upcoming"
              className="py-8 animate-fadein scroll-mt-32"
            >
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <h2 className="text-3xl font-bold mb-8 text-black animate-fadein font-manrope">
                  Upcoming Events
                </h2>
                {upcomingEvents.length === 0 ? (
                  <div className="text-gray-500 dark:text-gray-400 animate-fadein">
                    No upcoming events.
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 mb-16 animate-fadein">
                      {paginate(upcomingEvents, upcomingPage).map((event) => (
                        <div key={event.id} className="relative">
                          {/* Date badge like FeaturedEvents */}
                          {event.dateObj && (
                            <div className="absolute top-3 left-3 bg-amber-600 text-white rounded-lg px-3 py-1 text-sm shadow-lg">
                              {format(event.dateObj, "dd MMM")}
                            </div>
                          )}
                          <EventCard
                            event={{
                              ...event,
                              imageUrl: getImageUrl(event.imageUrl),
                            }}
                            handleSeeMore={handleSeeMore}
                            MAX_LENGTH={MAX_LENGTH}
                            onClick={() => handleCardClick(event)}
                          />
                        </div>
                      ))}
                    </div>
                    {totalPages(upcomingEvents) > 1 && (
                      <Pagination
                        currentPage={upcomingPage}
                        total={totalPages(upcomingEvents)}
                        onPageChange={setUpcomingPage}
                      />
                    )}
                  </>
                )}
              </div>
            </section>
          )}

          {/* Past Events */}
          {!loading && !error && (
            <section
              ref={pastRef}
              id="past"
              className="pt-0 pb-8 backdrop-blur-sm animate-fadein scroll-mt-32"
            >
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <h2 className="text-3xl font-bold mb-8 text-black animate-fadein font-manrope">
                  Past Events
                </h2>
                {pastEvents.length === 0 ? (
                  <div className="text-gray-500 dark:text-gray-400 animate-fadein">
                    No past events.
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 mb-16 animate-fadein">
                      {paginate(pastEvents, pastPage).map((event) => (
                        <EventCard
                          key={event.id}
                          event={{
                            ...event,
                            imageUrl: getImageUrl(event.imageUrl),
                          }}
                          handleSeeMore={handleSeeMore}
                          MAX_LENGTH={MAX_LENGTH}
                          onClick={() => handleCardClick(event)}
                        />
                      ))}
                    </div>
                    {totalPages(pastEvents) > 1 && (
                      <Pagination
                        currentPage={pastPage}
                        total={totalPages(pastEvents)}
                        onPageChange={setPastPage}
                      />
                    )}
                  </>
                )}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default Events;
